import { useEffect } from 'react';
import { X } from 'lucide-react';
import { useAuth } from '../../providers/AuthProvider';

interface KeyboardShortcutsModalProps {
  open: boolean;
  onClose: () => void;
}

interface Shortcut {
  keys: string[];
  label: string;
}

interface ShortcutGroup {
  title: string;
  note?: string;
  shortcuts: Shortcut[];
}

/**
 * App-wide keyboard shortcuts cheatsheet, grouped by surface. Opened from the
 * sidebar keyboard icon, the global Ctrl+/ hotkey (registered in Sidebar), and
 * the Queue Console (? key / action-bar help button).
 */
export function KeyboardShortcutsModal({ open, onClose }: KeyboardShortcutsModalProps) {
  const { effectiveRole } = useAuth();
  // Mirrors QueueConsolePage's skip gate — Esc-to-skip is admin+ only, so the
  // row is hidden from roles that can't use it.
  const canSkip = ['admin', 'auditor', 'csuite', 'platform-ops', 'agency_owner'].includes(
    effectiveRole,
  );

  // Close on Esc. In the Queue Console the page-level handler also closes us;
  // both call the same setter, so double-firing is harmless.
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, onClose]);

  if (!open) return null;

  const groups: ShortcutGroup[] = [
    {
      title: 'Queue Console',
      note: 'Keys pause while typing or while a popup is open',
      shortcuts: [
        { keys: ['Enter'], label: 'Complete contact (or load next when idle)' },
        { keys: ['Alt', '+', 'N'], label: 'Load next contact' },
        { keys: ['Alt', '+', 'C'], label: 'Call contact' },
        { keys: ['Alt', '+', 'S'], label: 'Open call script' },
        { keys: ['Alt', '+', 'F'], label: 'Schedule follow-up' },
        { keys: ['Alt', '+', 'T'], label: 'Focus message composer' },
        { keys: ['Alt', '+', 'R'], label: 'Reload current contact' },
        {
          keys: ['Alt', '+', 'D'],
          label: 'Toggle Do Not Disturb (confirm to enable · 1-press to clear)',
        },
        {
          keys: ['Esc'],
          label: canSkip ? 'Close popup · or skip contact if none is open' : 'Close popup',
        },
        { keys: ['?'], label: 'Show this help' },
      ],
    },
    {
      title: 'Disposition popup',
      shortcuts: [
        { keys: ['Enter'], label: 'Complete & move to next contact' },
        { keys: ['Esc'], label: 'Cancel without saving' },
      ],
    },
    {
      title: 'Conversations & messaging',
      shortcuts: [
        { keys: ['Enter'], label: 'Send message' },
        { keys: ['Shift', '+', 'Enter'], label: 'New line in a message' },
        { keys: ['Ctrl', '+', 'F'], label: 'Search messages in conversation' },
        { keys: ['Enter'], label: 'Next search match' },
        { keys: ['Shift', '+', 'Enter'], label: 'Previous search match' },
        { keys: ['End'], label: 'Jump to new messages (when indicator shows)' },
        { keys: ['Esc'], label: 'Close message search' },
      ],
    },
    {
      title: 'Recordings & voicemail',
      note: 'When an audio player is selected',
      shortcuts: [
        { keys: ['Space'], label: 'Play / pause' },
        { keys: ['←'], label: 'Skip back 5 seconds' },
        { keys: ['→'], label: 'Skip forward 5 seconds' },
      ],
    },
    {
      title: 'During a call',
      shortcuts: [{ keys: ['0–9', '*', '#'], label: 'Send keypad tones (phone menus)' }],
    },
    {
      title: 'Notes & editing',
      shortcuts: [
        { keys: ['Ctrl', '+', 'Enter'], label: 'Save note (Notes panel)' },
        { keys: ['Enter'], label: 'Save inline field edit' },
        { keys: ['Esc'], label: 'Cancel inline edit · clear bulk selection' },
      ],
    },
  ];

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-[2px] p-4"
      onPointerDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="bg-card border border-border/60 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col overflow-hidden">
        <div className="flex items-center justify-between px-5 py-3 border-b border-border/60 flex-shrink-0">
          <h2 className="text-[0.9375rem] font-bold text-foreground">Keyboard Shortcuts</h2>
          <button
            onClick={onClose}
            className="p-1 rounded-lg text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors cursor-pointer"
            aria-label="Close"
          >
            <X className="w-4 h-4 stroke-[1.5]" />
          </button>
        </div>
        <div className="p-4 overflow-y-auto grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4">
          {groups.map((g) => (
            <div key={g.title}>
              <h3 className="text-[0.6875rem] font-bold uppercase tracking-wider text-foreground mb-0.5">
                {g.title}
              </h3>
              {g.note && <p className="text-[0.6875rem] text-muted-foreground mb-1">{g.note}</p>}
              <ul className="space-y-2 mt-1.5">
                {g.shortcuts.map((s) => (
                  <li
                    key={s.label}
                    className="flex items-center justify-between gap-3 text-[0.8125rem]"
                  >
                    <span className="text-muted-foreground">{s.label}</span>
                    <span className="flex gap-1 flex-shrink-0">
                      {s.keys.map((k) => (
                        <kbd
                          key={k}
                          className="px-2 py-0.5 rounded-md bg-secondary text-foreground text-[0.6875rem] font-mono border border-border/60"
                        >
                          {k}
                        </kbd>
                      ))}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <div className="px-5 py-2.5 border-t border-border/60 text-[0.6875rem] text-muted-foreground flex-shrink-0">
          Open this from anywhere with{' '}
          <kbd className="px-1.5 py-0.5 rounded-md bg-secondary text-foreground font-mono border border-border/60">
            Ctrl
          </kbd>{' '}
          +{' '}
          <kbd className="px-1.5 py-0.5 rounded-md bg-secondary text-foreground font-mono border border-border/60">
            /
          </kbd>{' '}
          · on Mac, use <span className="font-mono">⌘</span> where Ctrl is shown.
        </div>
      </div>
    </div>
  );
}
